import React from "react"
import { ColorInput } from "core/color-picker"
import { Alignment } from "core/alignment"
import {
  GroupWrapper,
  RadioButtonGroup
} from "app/shared/components/radio-button-group"
import { Field } from "core/form"
import Slider from "@kit/design/slider"
import Grid from "@kit/design/grid"
import Stack from "@kit/design/stack"
import { LinkTriggerForm } from "../link-triggers/form"
import { analytics } from "utils/analytics"
import { useTrailingDebounce as useDebounce } from "hooks/use-trailing-debounce"

const SIZE_OPTIONS = [
  { text: "Small", value: "small" },
  { text: "Medium", value: "medium" },
  { text: "Large", value: "large" }
]

const ROUNDED_CORNER_OPTIONS = [
  { text: "None", value: "none" },
  { text: "Small", value: "small" },
  { text: "Large", value: "large" },
  { text: "Full", value: "full" }
]

const WIDTH_OPTIONS = [
  { text: "Auto", value: "auto" },
  { text: "Full", value: "100%" }
]

const normalizeRadius = radius => {
  if (!radius || (radius > 0 && radius <= 4)) {
    return "small"
  } else if (radius > 4 && radius <= 10) {
    return "large"
  } else if (radius > 10) {
    return "full"
  } else {
    return "default"
  }
}

export const ButtonMenu = ({ editor, node }) => {
  const [trackChange] = useDebounce(({ name, value }) => {
    analytics.event("block_change", {
      block_type: "button",
      property: name,
      value
    })
  }, 1000)

  const updateNode = ({ name, value }) => {
    trackChange({ name, value })

    editor.command("setNodeByKey", node.key, {
      data: { ...node.data.toJS(), [name]: value }
    })
  }

  const handleChange = event => {
    const { name, value } = event.target

    updateNode({ name, value })
  }

  const handleButtonClick = ({ name, value }) => {
    updateNode({ name, value })
  }

  const handleRadioClick = (name, value) => {
    updateNode({ name, value })
  }

  const size = node.data.get("size") || "medium"
  const alignment = node.data.get("alignment") || "center"
  const color = node.data.get("color") || "#ffffff"
  const backgroundColor = node.data.get("backgroundColor") || "#1677be"
  const margin = node.data.get("margin") || 0

  let width = node.data.get("width") || "auto"

  const borderRadius = node.data.get("borderRadius")
  let roundedCornerOption = node.data.get("roundedCornerOption")
  if (!roundedCornerOption) {
    roundedCornerOption = normalizeRadius(borderRadius)
  }

  return (
    <div className="flex">
      <div
        className="border-r border-gray-200 p-4"
        style={{ minWidth: "300px" }}
      >
        <LinkTriggerForm editor={editor} node={node} />
      </div>
      <div className="p-4">
        <Grid columns="repeat(2, 168px)" gap=".5rem" rowGap=".25rem">
          <Field
            control={ColorInput}
            label="Background color"
            name="backgroundColor"
            onChange={handleChange}
            value={backgroundColor}
          />
          <Field
            control={ColorInput}
            label="Text color"
            name="color"
            onChange={handleChange}
            value={color}
          />
          <Stack spacing={1}>
            <GroupWrapper label="Size">
              <RadioButtonGroup
                keyName="size"
                currentValue={size}
                update={handleRadioClick}
                options={SIZE_OPTIONS}
                buttonGroupClasses="w-full flex space-between justify-between bg-gray-100 border border-gray-200 rounded-sm text-gray-800"
                buttonClasses="text-gray-700 hover:text-gray-800 focus:text-gray-800 text-xs"
                activeButtonClasses="bg-blue-500 text-white text-xs"
                spacing={0}
              />
            </GroupWrapper>
          </Stack>
          <Stack spacing={1}>
            <GroupWrapper label="Rounded corners">
              <RadioButtonGroup
                keyName="roundedCornerOption"
                currentValue={roundedCornerOption}
                update={handleRadioClick}
                options={ROUNDED_CORNER_OPTIONS}
                buttonGroupClasses="w-full flex space-between justify-between bg-gray-100 border border-gray-200 rounded-sm text-gray-800"
                buttonClasses="text-gray-700 hover:text-gray-800 focus:text-gray-800 text-xs"
                activeButtonClasses="bg-blue-500 text-white text-xs"
                spacing={0}
              />
            </GroupWrapper>
          </Stack>
        </Grid>
        <Grid columns="repeat(3, 112px)" gap=".5rem" rowGap=".75rem">
          <Stack spacing={1}>
            <GroupWrapper label="Width">
              <RadioButtonGroup
                keyName="width"
                currentValue={width}
                update={handleRadioClick}
                options={WIDTH_OPTIONS}
                buttonGroupClasses="w-full flex space-between justify-between bg-gray-100 border border-gray-200 rounded-sm text-gray-800"
                buttonClasses="text-gray-700 hover:text-gray-800 focus:text-gray-800 text-xs"
                activeButtonClasses="bg-blue-500 text-white text-xs"
                spacing={0}
              />
            </GroupWrapper>
          </Stack>
          <Field
            control={Slider}
            label="Margin"
            name="margin"
            min={0}
            max={40}
            value={margin}
            onChange={handleChange}
            controlClassName="border border-gray-200 rounded-sm"
          />
          <Stack spacing={2}>
            <div className="inline-block text-sm font-semibold leading-snug text-gray-700">
              Alignment
            </div>
            <Alignment alignment={alignment} onChange={handleButtonClick} />
          </Stack>
        </Grid>
      </div>
    </div>
  )
}
